import { useMemo } from "react";
import { useLocation, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { DatesProvider } from "../../../../../../dates/DatesContext";
import moment from "moment";
import BreadcrumbDates, { type Breadcrumb } from "../../../../../../components/core/blocks/BreadcrumbDates";
import useCategories from "../../../../../../hooks/useCategories";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../../../../../../utils/dashboardLinks";
import { useModuleEnabled } from "../../../../../../redux/selectors/modules";
import checkTierAccess from "../../../../../../utils/checkTierAccess";
import PaymentDiscovery from "../../../../../../views/billing/PaymentDiscovery";
import EmptyState from "../../../../../../components/custom/merchandise/EmptyState";
import { useDates } from "../../../../../../dates/DatesContext";
import { useAppSelector } from "../../../../../../redux/hooks";
import PricingTables from "../../../../../../modules/pricingv2/components/PricingTables";
import { PageContainer } from "../../../../../../components/core/basic/PageContainer";
import { css } from "../../../../../../../styled-system/css";

const Dashboard = () => {
  const { t } = useTranslation(["pricingv2"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const { category } = Route.useParams();

  const { currentCategoryDetails: categoryDetails, categories: categoryData } = useCategories(
    category,
    "pricing/management",
  );

  const { group, start, end, preStart, preEnd } = useDates();

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    empty: true,
    group_by: "plan_id",
    category_id: category,
    category: category,
  };

  const breadCrumbItems: Breadcrumb[] = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    {
      name: t(($) => $.pricingv2["Pricing Dashboard"]),
      href: "/user/pricing/management",
      dropdownLinkOptions: DASHBOARD_LINKS,
      unsorted: true,
      searchable: false,
      dropdownSubtitle: t(($) => $.pricingv2["Dashboards"]),
    },
    {
      name: categoryDetails.leaf_category_name ?? t(($) => $.pricingv2["Unknown category"]),
      href: categoryDetails.href,
      dropdownLinkOptions: categoryData,
      searchable: true,
      searchPlaceholderText: t(($) => $.pricingv2["Search categories..."]),
      dropdownSubtitle: t(($) => $.pricingv2["Categories"]),
    },
  ];

  const showEmpty = useMemo(() => {
    const totalSalesStart = user.total_sales_start_date?.[marketPlace.marketPlace];
    if (totalSalesStart) {
      if (moment(totalSalesStart).isBefore(moment().subtract(2, "days"))) {
        return false;
      }
      return "COLLECTING_DATA";
    }
    return "NO_DATA";
  }, [user, marketPlace.marketPlace]);

  if (showEmpty) {
    return <EmptyState type={showEmpty} />;
  }

  return (
    <>
      <BreadcrumbDates items={breadCrumbItems} />
      <PageContainer withBreadcrumb padding={"large"}>
        <h3 className={css({ margin: 0 })}>{t(($) => $.pricingv2["Dynamic pricing management"])}</h3>
        <PricingTables marketPlace={marketPlace} category={category} filters={filters} />
      </PageContainer>
    </>
  );
};

const PricingManagementCategory = () => {
  const hasPriceOptimizationModule = useModuleEnabled("PRICE_OPTIMIZATION");

  const { category } = Route.useParams();
  const user = useAppSelector((state) => state.user);
  const amz_onboard = useAppSelector((state) => state.amz_onboard);

  const location = useLocation();

  const DYNAMIC_PRICING_SUBSCRIPTION =
    (((user.pricing_tier && user.pricing_tier !== "unknown") ||
      location.state.firstVisit ||
      location.state.onboardingComplete) &&
      (amz_onboard.org_status === "active" ||
        amz_onboard.org_status === "trial_active" ||
        amz_onboard.org_status === "demo_org")) ||
    hasPriceOptimizationModule ||
    checkTierAccess(user.tier, "pricing", amz_onboard.org_status);

  if (!DYNAMIC_PRICING_SUBSCRIPTION) {
    return <PaymentDiscovery discoveryType={"advertising"} dashboardType="pricing" />;
  }

  return (
    <DatesProvider>
      {/* Everything below is scoped to one category - product selection, status filter, unsaved
          inline price edits - and the route only changes this param, so React would otherwise keep
          it all across the navigation. Key inside DatesProvider so the date range still carries. */}
      <Dashboard key={category} />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/pricing/management/category/$category")({
  component: PricingManagementCategory,
});
